import React, { useState } from 'react';
import { Button } from '../components/Button';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { SparklesIcon, PlayIcon, StarIcon, QuoteIcon, ClockIcon, MessageSquareIcon, PhoneIcon, BellIcon, HelpCircleIcon, CalendarIcon, CheckIcon, PhoneForwardedIcon, MessageCircleIcon, CalendarCheckIcon, ZapIcon, WorkflowIcon, DatabaseIcon, BrainCircuitIcon, CheckCircleIcon, ShieldCheckIcon, AwardIcon, ChevronDownIcon } from 'lucide-react';
function BoltGradientHeroAnimated() {
  return <section className="w-full min-h-screen bg-[#0F0F0F] relative overflow-hidden flex items-center" data-id="element-323">
      {/* Animated Gradient Mesh Background */}
      <div className="absolute inset-0" data-id="element-324">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#F1B530] rounded-full mix-blend-multiply filter blur-[128px] opacity-20 animate-blob" data-id="element-325"></div>
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#D09F2B] rounded-full mix-blend-multiply filter blur-[128px] opacity-20 animate-blob animation-delay-2000" data-id="element-326"></div>
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-[#A78942] rounded-full mix-blend-multiply filter blur-[128px] opacity-20 animate-blob animation-delay-4000" data-id="element-327"></div>
      </div>

      <div className="max-w-7xl mx-auto px-8 md:px-12 py-20 relative z-10" data-id="element-328">
        <div className="text-center mb-16" data-id="element-329">
          <div className="inline-flex items-center gap-2 bg-[#252525]/80 backdrop-blur-md border border-[#F1B530]/30 rounded-full px-6 py-3 mb-8 animate-[fadeInUp_0.8s_ease-out]" data-id="element-330">
            <SparklesIcon className="w-4 h-4 text-[#F1B530]" data-id="element-331" />
            <span className="font-['Poppins'] text-sm text-[#F5F5F5] font-medium" data-id="element-332">
              AI-Powered Answering Service
            </span>
          </div>

          <h1 className="font-['Poppins'] font-bold text-5xl md:text-7xl lg:text-8xl text-[#F5F5F5] mb-8 leading-tight animate-[fadeInUp_0.8s_ease-out_0.2s] opacity-0 [animation-fill-mode:forwards]" data-id="element-333">
            <span className="bg-gradient-to-r from-[#F5F5F5] via-[#F1B530] to-[#F5F5F5] bg-clip-text text-transparent animate-gradient" data-id="element-334">
              Never Miss
            </span>
            <br data-id="element-335" />
            Another Call
          </h1>

          <p className="font-['Inter'] text-xl md:text-2xl text-[#A0A0A0] max-w-3xl mx-auto mb-12 leading-relaxed font-light animate-[fadeInUp_0.8s_ease-out_0.4s] opacity-0 [animation-fill-mode:forwards]" data-id="element-336">
            24/7 AI receptionist that answers instantly, captures perfectly, and
            integrates seamlessly with your business.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-[fadeInUp_0.8s_ease-out_0.6s] opacity-0 [animation-fill-mode:forwards]" data-id="element-337">
            <Button size="lg" data-id="element-338">Start Free Trial</Button>
            <Button variant="secondary" size="lg" data-id="element-339">
              Watch Demo
            </Button>
          </div>
        </div>

        {/* Floating Demo Card */}
        <div className="max-w-5xl mx-auto animate-[fadeInUp_0.8s_ease-out_0.8s] opacity-0 [animation-fill-mode:forwards]" data-id="element-340">
          <div className="bg-[#252525]/60 backdrop-blur-xl border border-[#2A2A2A] rounded-3xl p-8 shadow-2xl" data-id="element-341">
            <div className="aspect-video bg-[#1A1A1A] rounded-2xl flex items-center justify-center relative overflow-hidden" data-id="element-342">
              <img src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&q=80" alt="Dashboard preview" className="w-full h-full object-cover opacity-60" data-id="element-343" />
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-344">
                <div className="w-20 h-20 bg-[#F1B530] rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform duration-300 shadow-lg" data-id="element-345">
                  <PlayIcon className="w-8 h-8 text-[#0F0F0F] fill-[#0F0F0F] ml-1" data-id="element-346" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style data-id="element-347">{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s linear infinite;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>;
}
function BoltTrustBadgesAnimated() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const badges = [{
    icon: CheckCircleIcon,
    label: '99.9% Uptime',
    value: 'Guaranteed'
  }, {
    icon: ShieldCheckIcon,
    label: 'Enterprise Security',
    value: 'SOC 2 Certified'
  }, {
    icon: ClockIcon,
    label: '24/7 Support',
    value: 'Always Available'
  }, {
    icon: AwardIcon,
    label: '10,000+ Calls',
    value: 'Handled Daily'
  }];
  return <section className="w-full bg-[#1A1A1A] py-16" data-id="element-348">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-349">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-350">
          {badges.map((badge, index) => {
          const Icon = badge.icon;
          return <div key={index} className="text-center" data-id="element-351">
                <div className="w-16 h-16 bg-[#F1B530]/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-[#F1B530]/20" data-id="element-352">
                  <Icon className="w-8 h-8 text-[#F1B530]" data-id="element-353" />
                </div>
                <div className="font-['Poppins'] text-2xl font-bold text-[#F5F5F5] mb-1" data-id="element-354">
                  {badge.label}
                </div>
                <div className="font-['Inter'] text-sm text-[#A0A0A0]" data-id="element-355">
                  {badge.value}
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function BoltValueProposition() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const benefits = [{
    icon: ClockIcon,
    title: '24/7 Instant Call Answering',
    description: 'Never miss a call again with round-the-clock AI coverage that picks up instantly'
  }, {
    icon: MessageSquareIcon,
    title: 'Customised Company Greeting',
    description: 'Fully branded experience that sounds like your business, not a generic service'
  }, {
    icon: PhoneIcon,
    title: 'Accurate Capture',
    description: 'Precise name, number, and message recording with intelligent transcription'
  }, {
    icon: BellIcon,
    title: 'Real-Time Call Summaries',
    description: 'Instant notifications with complete call context delivered immediately'
  }, {
    icon: HelpCircleIcon,
    title: 'FAQ Handling',
    description: 'Answer common questions with fenced, brand-safe responses automatically'
  }, {
    icon: CalendarIcon,
    title: 'Appointment Booking',
    description: 'Seamless calendar integration for automated scheduling and confirmations'
  }];
  return <section className="w-full bg-[#0F0F0F] py-28 md:py-36 relative overflow-hidden" data-id="element-356">
      {/* Gradient accents */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#F1B530] opacity-10 rounded-full blur-3xl" data-id="element-357"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D09F2B] opacity-10 rounded-full blur-3xl" data-id="element-358"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-359">
        <div className="text-center mb-20" data-id="element-360">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl lg:text-6xl mb-6 tracking-tight" data-id="element-361">
            <span className="bg-gradient-to-r from-[#F5F5F5] via-[#F1B530] to-[#F5F5F5] bg-clip-text text-transparent" data-id="element-362">
              Why Choose Our AI Answering Service?
            </span>
          </h2>
          <p className="font-['Inter'] font-light text-xl md:text-2xl text-[#A0A0A0]" data-id="element-363">
            Your Always-On Receptionist, without the overheads
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-364">
          {benefits.map((benefit, index) => {
          const Icon = benefit.icon;
          return <div key={index} className="group relative bg-gradient-to-br from-[#1A1A1A] to-[#0F0F0F] rounded-2xl p-8 border border-[#2A2A2A] hover:border-[#F1B530]/50 transition-all duration-500 hover:-translate-y-2" data-id="element-365">
                {/* Gradient glow on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#F1B530]/0 to-[#F1B530]/0 group-hover:from-[#F1B530]/5 group-hover:to-[#D09F2B]/5 rounded-2xl transition-all duration-500" data-id="element-366"></div>

                <div className="relative" data-id="element-367">
                  {/* Icon with gradient background */}
                  <div className="w-16 h-16 bg-gradient-to-br from-[#F1B530] to-[#D09F2B] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-368">
                    <Icon className="w-8 h-8 text-[#0F0F0F]" data-id="element-369" />
                  </div>

                  <h3 className="font-['Poppins'] font-semibold text-xl text-[#F5F5F5] mb-4 group-hover:text-[#F1B530] transition-colors duration-300" data-id="element-370">
                    {benefit.title}
                  </h3>

                  <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light" data-id="element-371">
                    {benefit.description}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function BoltFeatureShowcase() {
  const feature1 = useScrollAnimation({
    threshold: 0.1
  });
  const feature2 = useScrollAnimation({
    threshold: 0.1
  });
  const feature3 = useScrollAnimation({
    threshold: 0.1
  });
  return <section className="w-full bg-[#1A1A1A] py-28 md:py-36 relative overflow-hidden" data-id="element-372">
      {/* Gradient accents */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-[#F1B530] opacity-10 rounded-full blur-3xl" data-id="element-373"></div>
      <div className="absolute bottom-40 left-20 w-80 h-80 bg-[#D09F2B] opacity-10 rounded-full blur-3xl" data-id="element-374"></div>

      <div className="max-w-7xl mx-auto px-8 md:px-12 relative z-10" data-id="element-375">
        {/* Feature 1 */}
        <div ref={feature1.ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32 transition-all duration-1000 ${feature1.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-376">
          <div className="relative group" data-id="element-377">
            <div className="absolute inset-0 bg-gradient-to-r from-[#F1B530] to-[#D09F2B] rounded-3xl opacity-20 blur-xl group-hover:opacity-30 transition-opacity duration-500" data-id="element-378"></div>
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-[#2A2A2A]" data-id="element-379">
              <img src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&q=80" alt="Real-time dashboard" className="w-full h-full object-cover" data-id="element-380" />
            </div>
            <div className="absolute -bottom-8 -right-8 bg-gradient-to-br from-[#F1B530] to-[#D09F2B] rounded-2xl p-6 shadow-2xl" data-id="element-381">
              <div className="font-['Poppins'] text-4xl font-bold text-[#0F0F0F] mb-1" data-id="element-382">
                100%
              </div>
              <div className="font-['Inter'] text-sm text-[#0F0F0F] font-semibold" data-id="element-383">
                Capture Rate
              </div>
            </div>
          </div>

          <div data-id="element-384">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6 bg-gradient-to-r from-[#F5F5F5] to-[#F1B530] bg-clip-text text-transparent" data-id="element-385">
              Real-Time Dashboard & Analytics
            </h2>
            <p className="font-['Inter'] text-lg text-[#A0A0A0] mb-8 leading-relaxed font-light" data-id="element-386">
              Monitor every call in real-time with comprehensive analytics.
              Track call volume, response times, customer satisfaction, and
              conversion rates all in one intuitive dashboard.
            </p>
            <ul className="space-y-4 mb-8" data-id="element-387">
              <li className="flex items-start" data-id="element-388">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-389">•</span>
                <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-390">
                  Live call monitoring and notifications
                </span>
              </li>
              <li className="flex items-start" data-id="element-391">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-392">•</span>
                <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-393">
                  Detailed call transcripts and recordings
                </span>
              </li>
              <li className="flex items-start" data-id="element-394">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-395">•</span>
                <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-396">
                  Performance metrics and insights
                </span>
              </li>
            </ul>
            <Button data-id="element-397">Explore Dashboard</Button>
          </div>
        </div>

        {/* Feature 2 */}
        <div ref={feature2.ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32 transition-all duration-1000 ${feature2.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-398">
          <div className="lg:order-2 relative group" data-id="element-399">
            <div className="absolute inset-0 bg-gradient-to-r from-[#D09F2B] to-[#F1B530] rounded-3xl opacity-20 blur-xl group-hover:opacity-30 transition-opacity duration-500" data-id="element-400"></div>
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-[#2A2A2A]" data-id="element-401">
              <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&q=80" alt="Integrations" className="w-full h-full object-cover" data-id="element-402" />
            </div>
            <div className="absolute -bottom-8 -left-8 bg-[#252525] border-2 border-[#F1B530] rounded-2xl p-6 shadow-2xl" data-id="element-403">
              <div className="font-['Poppins'] text-4xl font-bold text-[#F1B530] mb-1" data-id="element-404">
                50+
              </div>
              <div className="font-['Inter'] text-sm text-[#F5F5F5] font-semibold" data-id="element-405">
                Integrations
              </div>
            </div>
          </div>

          <div className="lg:order-1" data-id="element-406">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6 bg-gradient-to-r from-[#F5F5F5] to-[#F1B530] bg-clip-text text-transparent" data-id="element-407">
              Seamless CRM & Tool Integration
            </h2>
            <p className="font-['Inter'] text-lg text-[#A0A0A0] mb-8 leading-relaxed font-light" data-id="element-408">
              Connect with your existing tools effortlessly. Our AI integrates
              with popular CRMs, calendars, and business tools to keep your
              workflow smooth and data synchronized.
            </p>
            <ul className="space-y-4 mb-8" data-id="element-409">
              <li className="flex items-start" data-id="element-410">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-411">•</span>
                <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-412">
                  Salesforce, HubSpot, Zoho CRM
                </span>
              </li>
              <li className="flex items-start" data-id="element-413">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-414">•</span>
                <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-415">
                  Google Calendar, Outlook, Calendly
                </span>
              </li>
              <li className="flex items-start" data-id="element-416">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-417">•</span>
                <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-418">
                  Slack, Teams, Zapier, and more
                </span>
              </li>
            </ul>
            <Button data-id="element-419">View All Integrations</Button>
          </div>
        </div>

        {/* Feature 3 */}
        <div ref={feature3.ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center transition-all duration-1000 ${feature3.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-420">
          <div className="relative group" data-id="element-421">
            <div className="absolute inset-0 bg-gradient-to-r from-[#F1B530] to-[#D09F2B] rounded-3xl opacity-20 blur-xl group-hover:opacity-30 transition-opacity duration-500" data-id="element-422"></div>
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-[#2A2A2A]" data-id="element-423">
              <img src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&q=80" alt="AI training" className="w-full h-full object-cover" data-id="element-424" />
            </div>
            <div className="absolute -bottom-8 -right-8 bg-[#252525] border-2 border-[#F1B530] rounded-2xl p-6 shadow-2xl" data-id="element-425">
              <div className="font-['Poppins'] text-4xl font-bold text-[#F1B530] mb-1" data-id="element-426">
                &lt;5min
              </div>
              <div className="font-['Inter'] text-sm text-[#F5F5F5] font-semibold" data-id="element-427">
                Setup Time
              </div>
            </div>
          </div>

          <div data-id="element-428">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6 bg-gradient-to-r from-[#F5F5F5] to-[#F1B530] bg-clip-text text-transparent" data-id="element-429">
              Custom AI Training for Your Business
            </h2>
            <p className="font-['Inter'] text-lg text-[#A0A0A0] mb-8 leading-relaxed font-light" data-id="element-430">
              Train your AI receptionist with your business knowledge, FAQs, and
              specific protocols. The more you customize, the more natural and
              helpful your AI becomes.
            </p>
            <ul className="space-y-4 mb-8" data-id="element-431">
              <li className="flex items-start" data-id="element-432">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-433">•</span>
                <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-434">
                  Upload FAQs and knowledge base
                </span>
              </li>
              <li className="flex items-start" data-id="element-435">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-436">•</span>
                <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-437">
                  Define custom call routing rules
                </span>
              </li>
              <li className="flex items-start" data-id="element-438">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-439">•</span>
                <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-440">
                  Set brand voice and tone guidelines
                </span>
              </li>
            </ul>
            <Button data-id="element-441">Start Customizing</Button>
          </div>
        </div>
      </div>
    </section>;
}
function BoltIndustryBenefits() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const industries = [{
    name: 'Healthcare',
    benefits: ['HIPAA compliant', 'Appointment scheduling', 'Emergency routing', 'Patient messaging']
  }, {
    name: 'Legal',
    benefits: ['Client intake', 'Confidential handling', 'Urgent call screening', 'Case management']
  }, {
    name: 'Real Estate',
    benefits: ['Property inquiries', 'Viewing bookings', 'Lead capture', 'Tenant support']
  }, {
    name: 'Trade Services',
    benefits: ['Job booking', 'Emergency calls', 'Quote requests', 'Service scheduling']
  }, {
    name: 'Retail',
    benefits: ['Order taking', 'Store inquiries', 'Stock checks', 'Customer support']
  }, {
    name: 'Professional Services',
    benefits: ['Client screening', 'Appointment booking', 'Message handling', 'Consultation scheduling']
  }];
  return <section className="w-full bg-[#0F0F0F] py-28 relative overflow-hidden" data-id="element-442">
      {/* Gradient accents */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#F1B530] opacity-10 rounded-full blur-3xl" data-id="element-443"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D09F2B] opacity-10 rounded-full blur-3xl" data-id="element-444"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-445">
        <div className="text-center mb-16" data-id="element-446">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl mb-6 bg-gradient-to-r from-[#F5F5F5] via-[#F1B530] to-[#F5F5F5] bg-clip-text text-transparent" data-id="element-447">
            Trusted Across Industries
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light" data-id="element-448">
            Tailored solutions for every business type
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-449">
          {industries.map((industry, index) => <div key={index} className="group relative bg-gradient-to-br from-[#1A1A1A] to-[#0F0F0F] rounded-2xl p-8 border border-[#2A2A2A] hover:border-[#F1B530]/50 transition-all duration-500" data-id="element-450">
              <div className="absolute inset-0 bg-gradient-to-br from-[#F1B530]/0 to-[#F1B530]/0 group-hover:from-[#F1B530]/5 group-hover:to-[#D09F2B]/5 rounded-2xl transition-all duration-500" data-id="element-451"></div>

              <div className="relative" data-id="element-452">
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-6 group-hover:text-[#F1B530] transition-colors duration-300" data-id="element-453">
                  {industry.name}
                </h3>
                <ul className="space-y-3" data-id="element-454">
                  {industry.benefits.map((benefit, idx) => <li key={idx} className="font-['Inter'] text-[#A0A0A0] flex items-start" data-id="element-455">
                      <span className="text-[#F1B530] mr-2" data-id="element-456">•</span>
                      {benefit}
                    </li>)}
                </ul>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function BoltPricingPlans() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const plans = [{
    name: 'Scout 25',
    price: 'AUD $65',
    calls: '25 calls/month',
    minutes: '125',
    target: 'Perfect for solo operators testing the waters',
    inclusions: ['Instant call pickup', 'Customised company greeting', 'Name, number & message capture', 'Real-time call summaries via SMS/email', 'Call recordings & transcripts', 'Dashboard analytics', 'Excess: $2.60/call or $0.52/min'],
    ctaText: 'Start 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Forager 50',
    price: 'AUD $115',
    calls: '50 calls/month',
    minutes: '250',
    target: 'Small teams with steady enquiry flow',
    inclusions: ['Everything in Scout 25', 'Priority support', 'Extended call handling', 'Multi-user dashboard access', 'Custom call routing rules', 'Excess: $2.30/call or $0.46/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording',
    isPopular: true
  }, {
    name: 'Drift 75',
    price: 'AUD $165',
    calls: '75 calls/month',
    minutes: '375',
    target: 'Growing teams with rising call peaks',
    inclusions: ['Everything in Forager 50', 'Advanced analytics', 'Team collaboration tools', 'Custom integrations', 'Dedicated account manager', 'Excess: $2.20/call or $0.44/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Surge 150',
    price: 'AUD $325',
    calls: '150 calls/month',
    minutes: '750',
    target: 'High-tempo operations with regular spikes',
    inclusions: ['Everything in Drift 75', 'Priority call routing', 'Advanced workflow automation', 'Custom reporting', 'API access', 'Excess: $2.17/call or $0.43/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Swarm 300',
    price: 'AUD $645',
    calls: '300 calls/month',
    minutes: '1,500',
    target: 'Heavy daily call activity teams',
    inclusions: ['Everything in Surge 150', 'Enterprise-grade security', 'Custom AI training', 'White-label options', 'SLA guarantees', 'Excess: $2.15/call or $0.43/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Colony 500',
    price: 'AUD $955',
    calls: '500 calls/month',
    minutes: '2,250',
    target: 'Enterprise-level high-volume operations',
    inclusions: ['Everything in Swarm 300', 'Dedicated infrastructure', 'Custom AI models', 'Advanced compliance features', '24/7 premium support', 'Excess: $1.91/call or $0.42/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'AI Automation Customisation',
    target: 'For businesses requiring advanced automation and integrations beyond standard plans',
    inclusions: ['Custom call volumes', 'Bespoke AI training', 'Enterprise integrations', 'Advanced workflow automation', 'Dedicated success team', 'Custom SLAs'],
    ctaText: 'Contact Sales',
    isCustom: true
  }];
  return <section className="w-full bg-[#0F0F0F] py-28 md:py-36 relative overflow-hidden" data-id="element-457">
      {/* Subtle dot pattern background */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: 'radial-gradient(circle, #F1B530 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-458"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-459">
        <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] text-center mb-6 tracking-tight" data-id="element-460">
          Plans
        </h2>
        <p className="font-['Inter'] text-xl text-[#A0A0A0] text-center mb-20 font-light" data-id="element-461">
          Choose the perfect plan for your business needs
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-id="element-462">
          {plans.slice(0, 6).map((plan, index) => <div key={index} className={`bg-[#1A1A1A] rounded-2xl p-10 border transition-all duration-300 hover:-translate-y-1 relative flex flex-col h-full ${plan.isPopular ? 'border-[#F1B530] ring-2 ring-[#F1B530]/20' : 'border-[#2A2A2A] hover:border-[#F1B530]/30'}`} data-id="element-463">
              {plan.isPopular && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#F1B530] text-[#0F0F0F] px-6 py-2 rounded-full font-['Inter'] font-semibold text-sm" data-id="element-464">
                  Most Popular
                </div>}

              <div className="mb-8" data-id="element-465">
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-3" data-id="element-466">
                  {plan.name}
                </h3>
                {!plan.isCustom ? <>
                    <div className="mb-3" data-id="element-467">
                      <span className="font-['Poppins'] font-bold text-5xl text-[#F5F5F5]" data-id="element-468">
                        {plan.price}
                      </span>
                      <span className="text-[#A0A0A0] font-['Inter'] ml-2 text-sm" data-id="element-469">
                        plus GST/month
                      </span>
                    </div>
                    <p className="text-[#A0A0A0] font-['Inter'] text-sm mb-2 font-light" data-id="element-470">
                      Up to {plan.calls} • {plan.minutes} free minutes
                    </p>
                  </> : <div className="mb-3" data-id="element-471">
                    <span className="font-['Poppins'] font-bold text-4xl text-[#F5F5F5]" data-id="element-472">
                      Custom Pricing
                    </span>
                  </div>}
                <p className="text-[#A0A0A0] font-['Inter'] text-sm italic font-light" data-id="element-473">
                  {plan.target}
                </p>
              </div>

              <div className="mb-10 flex-grow" data-id="element-474">
                <ul className="space-y-4" data-id="element-475">
                  {plan.inclusions.map((inclusion, idx) => <li key={idx} className="flex items-start" data-id="element-476">
                      <CheckIcon className="w-5 h-5 text-[#F1B530] mr-3 flex-shrink-0 mt-0.5" data-id="element-477" />
                      <span className="font-['Inter'] text-[#F5F5F5] text-sm leading-relaxed font-light" data-id="element-478">
                        {inclusion}
                      </span>
                    </li>)}
                </ul>
              </div>

              <div className="mt-auto" data-id="element-479">
                <Button className="w-full" size="lg" data-id="element-480">
                  {plan.ctaText}
                </Button>

                {plan.ctaLink && <a href="#" className="block text-center mt-5 text-[#A0A0A0] font-['Inter'] text-sm hover:text-[#F1B530] transition-colors duration-300" data-id="element-481">
                    {plan.ctaLink}
                  </a>}
              </div>
            </div>)}
        </div>

        <div className="max-w-2xl mx-auto" data-id="element-482">
          <div className="bg-[#1A1A1A] rounded-2xl p-10 border border-[#2A2A2A] hover:border-[#F1B530]/30 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full" data-id="element-483">
            <div className="mb-8" data-id="element-484">
              <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-3" data-id="element-485">
                {plans[6].name}
              </h3>
              <div className="mb-3" data-id="element-486">
                <span className="font-['Poppins'] font-bold text-4xl text-[#F5F5F5]" data-id="element-487">
                  Custom Pricing
                </span>
              </div>
              <p className="text-[#A0A0A0] font-['Inter'] text-sm italic font-light" data-id="element-488">
                {plans[6].target}
              </p>
            </div>

            <div className="mb-10 flex-grow" data-id="element-489">
              <ul className="space-y-4" data-id="element-490">
                {plans[6].inclusions.map((inclusion, idx) => <li key={idx} className="flex items-start" data-id="element-491">
                    <CheckIcon className="w-5 h-5 text-[#F1B530] mr-3 flex-shrink-0 mt-0.5" data-id="element-492" />
                    <span className="font-['Inter'] text-[#F5F5F5] text-sm leading-relaxed font-light" data-id="element-493">
                      {inclusion}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="mt-auto" data-id="element-494">
              <Button className="w-full" size="lg" data-id="element-495">
                {plans[6].ctaText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function BoltTestimonials() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const testimonials = [{
    quote: "This AI answering service has transformed how we handle customer calls. We've seen a 40% increase in captured leads since implementing it.",
    author: 'Sarah Mitchell',
    role: 'CEO, TechStart Solutions',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80',
    rating: 5
  }, {
    quote: 'The 24/7 coverage means we never miss an opportunity. Our customers love the instant response, and so do we.',
    author: 'James Chen',
    role: 'Operations Director, GrowthCo',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80',
    rating: 5
  }, {
    quote: "Setup was incredibly easy, and the AI sounds so natural. It's like having a professional receptionist without the overhead costs.",
    author: 'Emma Thompson',
    role: 'Founder, Bright Ideas Agency',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80',
    rating: 5
  }];
  return <section className="w-full bg-[#1A1A1A] py-28 md:py-36 relative overflow-hidden" data-id="element-496">
      {/* Gradient accents */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-[#F1B530] opacity-10 rounded-full blur-3xl" data-id="element-497"></div>
      <div className="absolute bottom-40 right-20 w-80 h-80 bg-[#D09F2B] opacity-10 rounded-full blur-3xl" data-id="element-498"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-499">
        <div className="text-center mb-20" data-id="element-500">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6 bg-gradient-to-r from-[#F5F5F5] via-[#F1B530] to-[#F5F5F5] bg-clip-text text-transparent" data-id="element-501">
            Trusted by Growing Businesses
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light" data-id="element-502">
            See what our customers have to say
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-503">
          {testimonials.map((testimonial, index) => <div key={index} className="bg-[#252525]/50 backdrop-blur-sm border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300 hover:-translate-y-1" data-id="element-504">
              <div className="flex gap-1 mb-6" data-id="element-505">
                {[...Array(testimonial.rating)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-[#F1B530] fill-current" data-id="element-506" />)}
              </div>

              <QuoteIcon className="w-10 h-10 text-[#F1B530]/20 mb-4" data-id="element-507" />

              <p className="font-['Inter'] text-[#F5F5F5] mb-8 leading-relaxed font-light" data-id="element-508">
                "{testimonial.quote}"
              </p>

              <div className="flex items-center gap-4" data-id="element-509">
                <img src={testimonial.image} alt={testimonial.author} className="w-12 h-12 rounded-full object-cover border-2 border-[#F1B530]/20" data-id="element-510" />
                <div data-id="element-511">
                  <div className="font-['Inter'] font-semibold text-[#F5F5F5]" data-id="element-512">
                    {testimonial.author}
                  </div>
                  <div className="font-['Inter'] text-sm text-[#A0A0A0]" data-id="element-513">
                    {testimonial.role}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function BoltAddOns() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const addOns = [{
    icon: PhoneForwardedIcon,
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80',
    name: 'Live Transfer',
    price: 'AUD $99',
    setup: 'one-time setup',
    features: ['Instant Escalation for Time Sensitive Opportunities', 'Transfers With Full Caller Context', 'Smart Routing Based on Your Real Business Rules']
  }, {
    icon: MessageCircleIcon,
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80',
    name: 'FAQ Handling',
    price: 'AUD $299',
    setup: 'one-time fee',
    features: ['Turn AI agent into trained front-line knowledge assistant', 'Fenced responses using only approved information', 'Clear brand-safe responses', 'Faster resolution & reduced message volume']
  }, {
    icon: CalendarCheckIcon,
    image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=800&q=80',
    name: 'Appointment Booking',
    price: 'AUD $399',
    setup: 'one-time setup',
    features: ['Real-time calendar syncing (Google, Outlook, etc.)', 'Automated booking and confirmation', 'Custom booking rules', 'Caller notifications and internal alerts']
  }];
  return <section className="w-full bg-[#0F0F0F] py-28 md:py-36 relative overflow-hidden" data-id="element-514">
      {/* Vertical line accents */}
      <div className="absolute left-1/4 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#F1B530]/20 to-transparent" data-id="element-515"></div>
      <div className="absolute right-1/4 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#F1B530]/20 to-transparent" data-id="element-516"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-517">
        <div className="text-center mb-20" data-id="element-518">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6 tracking-tight" data-id="element-519">
            Universal Add-Ons
          </h2>
          <p className="font-['Inter'] text-lg text-[#A0A0A0] max-w-3xl mx-auto font-light" data-id="element-520">
            Available for all plans to extend your AI answering service
            capabilities
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-521">
          {addOns.map((addOn, index) => {
          const Icon = addOn.icon;
          return <div key={index} className="group bg-[#1A1A1A] rounded-2xl overflow-hidden border border-[#2A2A2A] hover:border-[#F1B530]/50 transition-all duration-300 hover:-translate-y-1" data-id="element-522">
                <div className="relative h-48 overflow-hidden bg-[#252525]" data-id="element-523">
                  <img src={addOn.image} alt={addOn.name} className="w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-300" data-id="element-524" />
                  <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/20 to-[#1A1A1A]" data-id="element-525"></div>
                  <div className="absolute bottom-4 left-4 w-14 h-14 bg-[#F1B530] rounded-xl flex items-center justify-center" data-id="element-526">
                    <Icon className="w-7 h-7 text-[#0F0F0F]" data-id="element-527" />
                  </div>
                </div>

                <div className="p-10" data-id="element-528">
                  <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-3" data-id="element-529">
                    {addOn.name}
                  </h3>

                  <div className="mb-8" data-id="element-530">
                    <span className="font-['Poppins'] font-semibold text-2xl text-[#F1B530]" data-id="element-531">
                      {addOn.price}
                    </span>
                    <span className="text-[#A0A0A0] font-['Inter'] text-sm ml-2" data-id="element-532">
                      {addOn.setup}
                    </span>
                  </div>

                  <ul className="space-y-4" data-id="element-533">
                    {addOn.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-534">
                        <span className="text-[#F1B530] mr-3 flex-shrink-0 text-lg" data-id="element-535">
                          •
                        </span>
                        <span className="font-['Inter'] text-[#F5F5F5] text-sm leading-relaxed font-light" data-id="element-536">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function BoltAdvancedAutomation() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const capabilities = [{
    icon: WorkflowIcon,
    text: 'Route leads intelligently'
  }, {
    icon: ZapIcon,
    text: 'Trigger automated workflows'
  }, {
    icon: DatabaseIcon,
    text: 'Sync directly into CRMs'
  }, {
    icon: BrainCircuitIcon,
    text: 'Collect structured information'
  }];
  return <section className="w-full bg-[#1A1A1A] py-28 md:py-36 relative overflow-hidden" data-id="element-537">
      {/* Radial gradient background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(241,181,48,0.05),transparent_50%)]" data-id="element-538"></div>

      <div ref={ref} className={`max-w-6xl mx-auto px-8 md:px-12 text-center relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-539">
        <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl lg:text-6xl mb-6 tracking-tight text-[#F5F5F5]" data-id="element-540">
          AI Automation Customisation Advanced
        </h2>
        <p className="font-['Inter'] font-light text-xl md:text-2xl text-[#A0A0A0] mb-10" data-id="element-541">
          Automate More Than Calls. Automate Your Entire Frontline.
        </p>

        <p className="font-['Inter'] text-lg text-[#A0A0A0] max-w-4xl mx-auto mb-16 leading-relaxed font-light" data-id="element-542">
          For businesses handling 500+ calls per month, unlock advanced
          capabilities that transform your AI answering service into a complete
          frontline automation platform.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-id="element-543">
          {capabilities.map((capability, index) => {
          const Icon = capability.icon;
          return <div key={index} className="bg-[#252525] rounded-2xl p-8 border border-[#2A2A2A] hover:border-[#F1B530]/30 transition-all duration-300 h-full flex flex-col items-center justify-center min-h-[180px] group" data-id="element-544">
                <div className="w-12 h-12 rounded-xl bg-[#F1B530]/10 flex items-center justify-center mb-5 group-hover:bg-[#F1B530]/20 transition-colors duration-300" data-id="element-545">
                  <Icon className="w-6 h-6 text-[#F1B530]" data-id="element-546" />
                </div>
                <p className="font-['Inter'] text-[#F5F5F5] font-light text-center" data-id="element-547">
                  {capability.text}
                </p>
              </div>;
        })}
        </div>

        <div className="mb-16 relative" data-id="element-548">
          <div className="bg-[#252525] rounded-2xl p-8 border border-[#2A2A2A] max-w-4xl mx-auto" data-id="element-549">
            <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80" alt="Automation workflow visualization" className="w-full h-64 object-cover rounded-xl opacity-50" data-id="element-550" />
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-551">
              <div className="bg-[#0F0F0F]/80 backdrop-blur-sm border border-[#F1B530]/50 rounded-xl px-8 py-4" data-id="element-552">
                <p className="font-['Inter'] text-[#F1B530] font-semibold text-lg" data-id="element-553">
                  Complete Workflow Automation
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#252525] rounded-2xl p-10 border border-[#2A2A2A] mb-12 max-w-3xl mx-auto" data-id="element-554">
          <h3 className="font-['Inter'] font-semibold text-xl mb-6 text-[#F5F5F5]" data-id="element-555">
            Extended Capabilities Include:
          </h3>
          <ul className="font-['Inter'] text-[#A0A0A0] space-y-3 text-left max-w-2xl mx-auto font-light" data-id="element-556">
            <li data-id="element-557">• Support multi-step decision trees</li>
            <li data-id="element-558">• Extended conversational capabilities</li>
            <li data-id="element-559">• Custom API integrations</li>
            <li data-id="element-560">• Advanced data collection and processing</li>
            <li data-id="element-561">• Bespoke workflow automation</li>
          </ul>
        </div>

        <Button size="lg" data-id="element-562">Contact Sales</Button>
      </div>
    </section>;
}
function BoltFAQ() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: 'How does the AI answering service work?',
    answer: 'Our AI receptionist answers calls 24/7, captures caller information, handles FAQs, and can even book appointments. It integrates seamlessly with your existing systems and can be customized to match your business needs.'
  }, {
    question: 'Can I customize the greeting and responses?',
    answer: 'Absolutely! You can fully customize your company greeting, upload FAQs, set routing rules, and define your brand voice. The AI learns from your business to provide accurate, on-brand responses.'
  }, {
    question: "What happens if the AI can't handle a call?",
    answer: 'The AI can transfer calls to your team with full context when needed. You set the rules for when calls should be escalated, ensuring important matters always reach the right person.'
  }, {
    question: 'How quickly can I get started?',
    answer: "Setup takes less than 5 minutes. Simply sign up, customize your greeting and settings, forward your number, and you're live. We provide step-by-step guidance throughout the process."
  }, {
    question: 'Is there a contract or commitment?',
    answer: 'No long-term contracts required. Start with our 7-day free trial, then choose a monthly plan that fits your needs. You can upgrade, downgrade, or cancel anytime.'
  }];
  return <section className="w-full bg-[#1A1A1A] py-28 md:py-36 relative overflow-hidden" data-id="element-563">
      {/* Gradient accents */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-[#F1B530] opacity-10 rounded-full blur-3xl" data-id="element-564"></div>
      <div className="absolute bottom-40 right-20 w-80 h-80 bg-[#D09F2B] opacity-10 rounded-full blur-3xl" data-id="element-565"></div>

      <div ref={ref} className={`max-w-4xl mx-auto px-8 md:px-12 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-566">
        <div className="text-center mb-16" data-id="element-567">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl mb-6 bg-gradient-to-r from-[#F5F5F5] via-[#F1B530] to-[#F5F5F5] bg-clip-text text-transparent" data-id="element-568">
            Frequently Asked Questions
          </h2>
          <p className="font-['Inter'] text-lg text-[#A0A0A0] font-light" data-id="element-569">
            Everything you need to know about our AI answering service
          </p>
        </div>

        <div className="space-y-4" data-id="element-570">
          {faqs.map((faq, index) => <div key={index} className="bg-[#252525]/50 backdrop-blur-sm border border-[#2A2A2A] rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#F1B530]/30" data-id="element-571">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full px-8 py-6 flex items-center justify-between text-left" data-id="element-572">
                <span className="font-['Poppins'] font-semibold text-xl text-[#F5F5F5] pr-8" data-id="element-573">
                  {faq.question}
                </span>
                <ChevronDownIcon className={`w-6 h-6 text-[#F1B530] flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-574" />
              </button>

              <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`} data-id="element-575">
                <div className="px-8 pb-6" data-id="element-576">
                  <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light" data-id="element-577">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
export function BoltInspired() {
  return <div className="w-full min-h-screen bg-[#0F0F0F]" data-id="element-578">
      <BoltGradientHeroAnimated data-id="element-579" />
      <BoltTrustBadgesAnimated data-id="element-580" />
      <BoltValueProposition data-id="element-581" />
      <BoltFeatureShowcase data-id="element-582" />
      <BoltIndustryBenefits data-id="element-583" />
      <BoltPricingPlans data-id="element-584" />
      <BoltTestimonials data-id="element-585" />
      <BoltAddOns data-id="element-586" />
      <BoltAdvancedAutomation data-id="element-587" />
      <BoltFAQ data-id="element-588" />
    </div>;
}